import { keoChap } from "../../../helpers/dieuKhoanTran";
import * as SBService from "../../../helpers/sportbookService";
import { asArray, dateRange, shortName, unwrap } from "../shared/format";
import {
  DeCurrentMatch,
  DeLeagueInfo,
  DeMe,
  DeNode,
  DePrize,
  DeStage,
  DeStandingRow,
  DeVenue,
  DeView,
} from "./types";

/**
 * Lấy dữ liệu trang giải Đấu kép.
 *
 * Theo ĐÚNG mẫu chịu lỗi của fetchSwissView, và mẫu đó ra đời từ một lỗi có thật: một
 * lỗi lẻ ở /me, /bracket hay bảng xếp hạng từng làm CẢ TRANG báo "không tìm thấy giải".
 * Nên ở đây chỉ `overview` là bắt buộc; ba phần còn lại hỏng thì khuyết đúng khối đó.
 *
 * Và bản có token hỏng thì rơi về bản công khai: người xem mất phần "của bạn" nhưng
 * vẫn thấy giải, sơ đồ, bảng xếp hạng.
 */

const mapLeague = (overview: any): DeLeagueInfo => {
  const league = overview?.league ?? {};

  return {
    id: String(league.id ?? ""),
    name: league.name ?? "",
    // Nhãn thể thức do backend trả ("Đấu kép"); không gõ lại ở front office.
    format: league.variantLabel ?? "Đấu kép",
    race: Number(league.race ?? 7),
    koMode: league.koMode ?? "pre",
    players: Number(league.numberParticipants ?? 0),
    registered: Number(overview?.totals?.registered ?? 0),
    official: Number(overview?.totals?.official ?? overview?.totals?.registered ?? 0),
    pendingFee: Number(overview?.totals?.pendingFee ?? 0),
    fee: league.fee ?? null,
    hasFee: !!league.hasFee,
    feeQrImage: league.feeQrImage ?? null,
    feeTransferNote: league.feeTransferNote ?? null,
    feeDeadlineDate: league.feeDeadlineDate ?? null,
    rule: league.rule ?? null,
    logo: league.logo ?? null,
    banner: league.banner ?? null,
    reg: dateRange(league.startRegistrationDate, league.endRegistrationDate),
    play: dateRange(league.startDate, league.endDate),
    startRegDate: league.startRegistrationDate ?? null,
    endRegDate: league.endRegistrationDate ?? null,
    startDate: league.startDate ?? null,
    endDate: league.endDate ?? null,
    isFinished: !!league.isFinished,
  };
};

const mapVenues = (venues: any): DeVenue[] =>
  asArray(venues).map((venue) => ({
    id: String(venue.venueId ?? venue.id ?? ""),
    name: venue.name ?? "",
    short: shortName(venue.name || ""),
    cap: venue.capacity ?? venue.cap ?? null,
    taken: Number(venue.taken ?? 0),
  }));

const mapPrizes = (prizes: any): DePrize[] =>
  asArray(prizes).map((prize) => ({
    label: prize.label ?? "",
    cash: prize.prizeInCash ?? prize.cash ?? null,
    kind: prize.prizeInKind ?? prize.kind ?? null,
  }));

const mapNode = (node: any): DeNode => ({
  code: node.code ?? "",
  // Ô chưa có cặp vẫn có nodeId; rơi về mã ô để sơ đồ không mất khoá.
  nodeId: String(node.nodeId ?? node.code ?? ""),
  stage: Number(node.stage ?? 0),
  idx: Number(node.idx ?? 0),
  winToId: node.winToId ?? null,
  winToSlot: node.winToSlot ?? null,
  loseToId: node.loseToId ?? null,
  loseToSlot: node.loseToSlot ?? null,
  scheduleId: node.scheduleId ?? null,
  matchId: node.matchId ?? null,
  status: node.status ?? "LIVE",
  isBye: !!node.isBye,
  mine: !!node.mine,
  table: node.table ?? null,
  venueName: node.venueName ?? null,
  race: Number(node.race ?? 0),
  players: asArray(node.players).map((player: any) => ({
    id: player.playerId ?? player.id ?? null,
    name: player.name ?? "",
    score: player.score === null || player.score === undefined ? null : Number(player.score),
    isWinner: !!player.isWinner,
    byeSeat: !!player.byeSeat,
  })),
  // Dựng ở đây vì chỉ ở đây còn biết ghế nào là người thách - `players` theo thứ tự
  // ô sơ đồ, còn người thách trong app có thể ngồi ghế dưới. Như swiss/api.ts.
  keo: keoChap(node, tenGhe(node, node.challengerId), tenGhe(node, node.acceptorId)),
});

/** Tên của một ghế theo id, hoặc chuỗi rỗng khi ô chưa đủ người. */
const tenGhe = (node: any, playerId: string | null | undefined): string =>
  asArray(node.players).find((player: any) => (player.playerId ?? player.id) === playerId)?.name || "";

const mapStages = (bracket: any): DeStage[] =>
  asArray(bracket?.stages).map((stage: any) => ({
    stage: Number(stage.stage ?? 0),
    branch: stage.branch ?? "",
    name: stage.name ?? "",
    nodes: asArray(stage.matches).map(mapNode),
  }));

const mapStandings = (standings: any): DeStandingRow[] =>
  asArray(standings?.rows).map((row: any) => ({
    playerId: String(row.playerId ?? ""),
    name: row.name ?? "",
    avatar: row.avatar ?? null,
    rankClass: row.rankClass ?? null,
    rating: row.rating === null || row.rating === undefined ? null : Number(row.rating),
    rank: row.rank === null || row.rank === undefined ? null : Number(row.rank),
    rankLabel: row.rankLabel ?? "—",
    note: row.note ?? null,
    lives: Number(row.lives ?? 0),
    branch: row.branch ?? null,
  }));

const mapMatch = (match: any): DeCurrentMatch | null => {
  if (!match) {
    return null;
  }

  return {
    scheduleId: String(match.scheduleId ?? ""),
    matchId: match.matchId ?? null,
    opponentId: match.opponentId ?? null,
    opponentName: match.opponentName ?? null,
    opponentAvatar: match.opponentAvatar ?? null,
    opponentRankClass: match.opponentRankClass ?? null,
    opponentRating:
      match.opponentRating === null || match.opponentRating === undefined ? null : Number(match.opponentRating),
    roundLabel: match.roundLabel ?? null,
    challengerId: match.challengerId ?? null,
    acceptorId: match.acceptorId ?? null,
    race: Number(match.race ?? 0),
    table: match.table ?? null,
    venueName: match.venueName ?? null,
    started: !!match.started,
    isWaitingRivalConfirmed: !!match.isWaitingRivalConfirmed,
    challenge: match.challenge
      ? { initiatorId: match.challenge.initiatorId ?? null, byMe: !!match.challenge.byMe }
      : null,
    pendingResult: match.pendingResult
      ? {
          id: String(match.pendingResult.id ?? ""),
          submitterId: match.pendingResult.submitterId ?? null,
          challengerPoint: Number(match.pendingResult.challengerPoint ?? 0),
          acceptorPoint: Number(match.pendingResult.acceptorPoint ?? 0),
          submittedByMe: !!match.pendingResult.submittedByMe,
        }
      : null,
  };
};

const mapMe = (me: any, overview: any, playerId: string | null): DeMe => {
  const registration = overview?.myRegistration ?? null;

  return {
    state: me?.state ?? (registration ? "waiting_start" : "not_joined"),
    lives: me?.lives === null || me?.lives === undefined ? null : Number(me.lives),
    branch: me?.branch ?? null,
    vongChungKet: !!me?.vongChungKet,
    avatar: me?.avatar ?? null,
    rankClass: me?.rankClass ?? null,
    rating: me?.rating === null || me?.rating === undefined ? null : Number(me.rating),
    duocMienDau: !!me?.duocMienDau,
    match: mapMatch(me?.match),
    /* Địa điểm, đóng phí và check-in lấy từ overview chứ không từ /me: overview biết
       đăng ký của tôi kể cả khi /me hỏng, và đó chính là khối người dùng cần nhất khi
       chưa đóng phí. */
    venueId: registration?.venueId ?? null,
    isFeePaid: !!registration?.isFeePaid,
    isOfficial: !!registration?.isOfficial,
    isCheckin: !!registration?.isCheckin,
    billUploaded: !!registration?.billUploaded,
    billImage: registration?.billImage ?? null,
    /* Đã đăng ký suy theo overview, KHÔNG theo me.state: /me hỏng thì state rơi về
       "not_joined" và người đã đăng ký bị đẩy về màn đăng ký - thấy nút "Tham gia
       ngay" cho một giải mình đã có suất. */
    registered: !!registration || (!!me?.state && me.state !== "not_joined"),
    playerId,
  };
};

export const fetchDeView = async (leagueId: string, playerId: string | null): Promise<DeView> => {
  const authed = !!playerId;

  /* Chỉ overview là bắt buộc: thiếu nó thì không có gì để hiển thị. Ba phần còn lại
     hỏng thì trang vẫn phải mở được. */
  const soft = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => fallback);

  const overviewOf = async () => {
    if (!authed) {
      return await SBService.getPublicDeOverview(leagueId);
    }
    try {
      return await SBService.getDeOverview(leagueId);
    } catch (e) {
      return await SBService.getPublicDeOverview(leagueId);
    }
  };

  const bracketOf = async () => {
    if (!authed) {
      return await soft(SBService.getPublicDeBracket(leagueId), null);
    }
    try {
      return await SBService.getDeBracket(leagueId);
    } catch (e) {
      return await soft(SBService.getPublicDeBracket(leagueId), null);
    }
  };

  const standingsOf = async () => {
    if (!authed) {
      return await soft(SBService.getPublicDeStandings(leagueId), null);
    }
    try {
      return await SBService.getDeStandings(leagueId);
    } catch (e) {
      return await soft(SBService.getPublicDeStandings(leagueId), null);
    }
  };

  const [overviewRsp, bracketRsp, standingsRsp, meRsp] = await Promise.all([
    overviewOf(),
    bracketOf(),
    standingsOf(),
    authed ? soft(SBService.getDeMyStatus(leagueId), null) : Promise.resolve(null),
  ]);

  const overview = unwrap(overviewRsp);
  const bracket = unwrap(bracketRsp);
  const standings = unwrap(standingsRsp);
  const me = unwrap(meRsp);

  return {
    league: mapLeague(overview),
    venues: mapVenues(overview?.venues),
    prizes: mapPrizes(overview?.prizes),
    stages: mapStages(bracket),
    drawn: !!bracket?.drawn,
    standings: mapStandings(standings),
    me: mapMe(me, overview, playerId),
  };
};

/* Chỉ để bài test gọi thẳng hai hàm ánh xạ. Chúng không có phụ thuộc nào ngoài dữ
   liệu truyền vào, nên kiểm được mà không cần dựng cả trang. */
export const __test = { mapStandings, mapMatch, mapNode };
